import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import portalService from '../../services/portalService';
import StatusBadge from '../shared/StatusBadge';
import { formatCurrency } from '../../utils/amountUtils';
import { formatDate as formatDateSafe } from '../../utils/dateUtils';

// Normalize card brand to title case — "VISA CREDIT" → "Visa Credit"
function titleCase(str) {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function PortalInvoiceHistory() {
  const context = useOutletContext();
  const publicId = context?.customerPublicId || context?.customerInfo?.customer?.publicId || context?.portalData?.customerPublicId;
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' or 'invoices'
  
  // Transaction states
  const [transactions, setTransactions] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [transactionPagination, setTransactionPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [invoicePagination, setInvoicePagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  

  // Fetch transactions with refunds
  const fetchTransactions = useCallback(async () => {
    if (!publicId) return;
    
    try {
      setTransactionLoading(true);
      const response = await portalService.getPaymentHistoryWithRefunds(publicId, {
        page: transactionPagination.page,
        limit: transactionPagination.limit
      });
      
      setTransactions(response.payments || []);
      setTransactionPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setTransactionLoading(false);
    }
  }, [publicId, transactionPagination.page, transactionPagination.limit]);

  // Fetch invoices only (no refunds in Invoice History)
  const fetchInvoices = useCallback(async () => {
    if (!publicId) return;
    
    try {
      setInvoiceLoading(true);
      
      const params = {
        status: filter,
        page: invoicePagination.page,
        limit: invoicePagination.limit
      };
      
      if (dateRange.startDate) params.dateFrom = dateRange.startDate;
      if (dateRange.endDate) params.dateTo = dateRange.endDate;
      
      const invoicesResponse = await portalService.getInvoices(publicId, params);
      
      setInvoices(invoicesResponse.invoices || []);
      setInvoicePagination(prev => ({
        ...prev,
        total: invoicesResponse.pagination?.total || 0,
        pages: invoicesResponse.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setInvoiceLoading(false);
    }
  }, [publicId, filter, dateRange, invoicePagination.page, invoicePagination.limit]);

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    } else {
      fetchInvoices();
    }
  }, [activeTab, fetchTransactions, fetchInvoices]);


  // Thin wrapper around the shared timezone-safe formatter — preserves this
  // screen's 'N/A' fallback and short-month format (other portal screens use long).
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateSafe(dateString, { year: 'numeric', month: 'short', day: 'numeric' });
  };


  const handleDateRangeChange = (field, value) => {
    const newRange = { ...dateRange, [field]: value };
    setDateRange(newRange);
    setInvoicePagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Payment History</h2>
        <p className="text-gray-600 mt-1">View your transaction and invoice history</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('transactions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'transactions'
                ? 'border-brand text-brand'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Transactions
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invoices'
                ? 'border-brand text-brand'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Invoice History
          </button>
        </nav>
      </div>

      {/* Transaction History Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {transactionLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                There are no payment transactions to display.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((payment) => {
                    // Check if this is a credit application
                    const isCreditApplication = payment.recordType === 'credit' ||
                                                payment.transactionType === 'credit_application' ||
                                                payment.paymentMethod === 'credit_memo';

                    // Determine transaction type and display
                    const ref = payment.transactionReference || payment.reference || payment.transactionId || payment.id;
                    let transactionDisplay = ref;
                    let isRefund = payment.transactionType === 'refund';

                    // Determine the type based on reference and invoice linkage
                    if (isCreditApplication) {
                      // Show credit memo number if available
                      transactionDisplay = payment.creditMemoNumber
                        ? `Credit - ${payment.creditMemoNumber}`
                        : `Credit Applied - ${ref}`;
                    } else if (isRefund) {
                      transactionDisplay = `Refund - ${ref}`;
                    } else if (ref && ref.startsWith('EXT-')) {
                      transactionDisplay = `External - ${ref}`;
                    } else if (payment.isLinked || payment.allocatedAmount > 0 || payment.invoiceNumber) {
                      // Platform transactions linked to invoices (either directly or through invoice_payments)
                      transactionDisplay = `Linked - ${ref}`;
                    }
                    // else just show the transaction ID without prefix (unlinked platform transactions)

                    // Total charged = base + surcharge (shows what customer actually paid)
                    const surchargeAmount = parseFloat(payment.surchargeAmount || 0);
                    const displayAmount = parseFloat(payment.amount || 0) + surchargeAmount;

                    return (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(payment.paymentDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transactionDisplay}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {isCreditApplication ? (
                            <span className="text-green-600 font-medium">Credit Memo</span>
                          ) : (
                            titleCase(payment.brand) || '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {payment.invoiceNumber || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {isRefund ? (
                            <span className="text-orange-600">-{formatCurrency(payment.amount)}</span>
                          ) : isCreditApplication ? (
                            <span className="text-green-600">{formatCurrency(payment.amount)}</span>
                          ) : (
                            formatCurrency(displayAmount)
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isCreditApplication ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Applied
                            </span>
                          ) : (
                            <StatusBadge
                              type="transaction_status"
                              value={{
                                status: payment.status,
                                transactionType: payment.transactionType,
                                isVoided: payment.paymentStatus === 'voided'
                              }}
                              size="xs"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>

              {/* Transaction Pagination */}
              {transactionPagination.pages > 1 && (
                <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((transactionPagination.page - 1) * transactionPagination.limit) + 1} to{' '}
                    {Math.min(transactionPagination.page * transactionPagination.limit, transactionPagination.total)} of{' '}
                    {transactionPagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTransactionPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={transactionPagination.page === 1}
                      className={`px-3 py-1 text-sm rounded-md ${
                        transactionPagination.page === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setTransactionPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={transactionPagination.page === transactionPagination.pages}
                      className={`px-3 py-1 text-sm rounded-md ${
                        transactionPagination.page === transactionPagination.pages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Invoice History Tab */}
      {activeTab === 'invoices' && (
        <>
          {/* Invoice Filters */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setInvoicePagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand"
                >
                  <option value="all">All Invoices</option>
                  <option value="paid">Paid Only</option>
                  <option value="open">Unpaid Only</option>
                  <option value="overdue">Overdue Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>

              {(dateRange.startDate || dateRange.endDate) && (
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setDateRange({ startDate: '', endDate: '' });
                      setInvoicePagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    Clear dates
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {invoiceLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
              </div>
            ) : invoices.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
                  />
                </svg>
                <p className="mt-2 text-gray-600">No invoices found</p>
                <p className="mt-1 text-sm text-gray-500">
                  There are no invoices matching your current filters.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoices.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {invoice.invoiceNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(invoice.invoiceDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(invoice.dueDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <StatusBadge 
                              type="invoice_status" 
                              value={{
                                status: invoice.status,
                                paymentStatus: invoice.paymentStatus,
                                dueDate: invoice.dueDate,
                                displayStatus: invoice.displayStatus
                              }}
                              size="xs"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                            <span className="text-gray-900">{formatCurrency(invoice.totalAmount)}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <button
                              onClick={() => navigate(`../invoices/${invoice.invoiceNumber}`)}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Invoice Pagination */}
                {invoicePagination.pages > 1 && (
                  <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((invoicePagination.page - 1) * invoicePagination.limit) + 1} to{' '}
                      {Math.min(invoicePagination.page * invoicePagination.limit, invoicePagination.total)} of{' '}
                      {invoicePagination.total} results
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setInvoicePagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={invoicePagination.page === 1}
                        className={`px-3 py-1 text-sm rounded-md ${
                          invoicePagination.page === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setInvoicePagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={invoicePagination.page === invoicePagination.pages}
                        className={`px-3 py-1 text-sm rounded-md ${
                          invoicePagination.page === invoicePagination.pages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default PortalInvoiceHistory;